import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getSnapshotDetails } from "../../../app/api/trendSnapshot";
import { RootState } from "../../../app/store";
import { ReactComponent as LeftArrow } from "../../../assets/icons/leftArrow.svg";
import { DataNode } from "../../../interface/Trends";
import ColorAnalysisSnapShot from "./ColorAnalysisSnapshot";
import Dropdown from "../../Common/Dropdown";
import { handleDownloadColorAnalysis } from "../../../utils/trendSidebarGraphUtils/colorAnalysisUtils";
import { ReactComponent as Download } from "../../../assets/icons/download.svg";

interface ExpandViewColorAnalysisProps {
  isSidebarOpen?: boolean;
}

const ExpandedViewSnapshotColorAnalysis: React.FC<
  ExpandViewColorAnalysisProps
> = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const heightInPx = window.innerHeight - 140;
  const [loading, setLoading] = useState(false);
  const user = useSelector((state: RootState) => state.user.userInfo);

  const { id } = useParams();
  const [l1AnalysisData, setL1AnalysisData] = useState<DataNode | null>(null);
  const [l2AnalysisData, setL2AnalysisData] = useState<DataNode | null>(null);
  const [l3AnalysisData, setL3AnalysisData] = useState<DataNode | null>(null);
  const [colorAnalysisFilterType, setColorAnalysisFilterType] =
    useState<string>("Most Popular");
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const numericSnapshotId = Number(id);
  const fetchDetails = async (page: number) => {
    const detailsParams = {
      page: page,
      numberOfTrends: 1,
      postsPerTrend: 1,
    };
    setLoading(true);
    try {
      const snapDetails = await getSnapshotDetails(
        numericSnapshotId,
        user,
        detailsParams
      );

      setColorAnalysisFilterType(
        snapDetails?.data?.snapshot?.genericFilters?.graphsFilters
          ?.color_query_type || "Most Popular"
      );

      //Set L1 Color Analysis Data
      if (snapDetails.data.l1ColorAnalysis) {
        const dataL1: DataNode = snapDetails.data.l1ColorAnalysis;
        setL1AnalysisData(dataL1);
      }

      // Set L2 Color Analysis Data
      if (snapDetails.data.l2ColorAnalysis) {
        const dataL2: DataNode = snapDetails.data.l2ColorAnalysis.children;
        setL2AnalysisData(dataL2);
        //Set L3 Color Analysis Data
        const colorForLevel3 =
          snapDetails?.data?.snapshot?.genericFilters?.trendAnalysisFilters
            ?.colorForLevel3;
        if (colorForLevel3) {
          const dataL3 = dataL2?.children?.find(
            d => d.color === colorForLevel3
          );
          if (dataL3) {
            setL3AnalysisData(dataL3 || null);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching snapshot details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails(1);
  }, []);

  if (l1AnalysisData && Object.keys(l1AnalysisData).length === 0) {
    return <div className="my-10">No Color Analysis Data</div>;
  }

  return (
    <div
      className={`bg-white fixed h-[calc(100vh-80px)] ml-2 ${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"} transition-all duration-500 ease-in-out`}
    >
      <div className="flex flex-row justify-between mb-4">
        <div className="flex flex-row mt-6">
          <div>
            <LeftArrow
              className="text-lg ml-4 mt-4 cursor-pointer"
              onClick={() => navigate(`/trend-snapshot/details/${id}`)}
            />
          </div>
          <div className="transition-all  duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-4 mt-2.5 text-secondary ">
            Color Analysis
          </div>
        </div>
        <div className="mt-2 mr-4 flex">
          {downloadLoading ? (
            <p className="mr-5 mt-4">Loading...</p>
          ) : (
            <button
              onClick={() =>
                handleDownloadColorAnalysis(
                  l1AnalysisData,
                  l2AnalysisData,
                  l3AnalysisData,
                  colorAnalysisFilterType,
                  setDownloadLoading
                )
              }
            >
              <Download width={25} height={25} className="mr-5 mt-4" />
            </button>
          )}
          <div>
            <div className="text-sm">Filter By</div>
            <div className="w-48">
              <Dropdown
                label={colorAnalysisFilterType}
                options={[]}
                onSelect={option => {}}
                trendSnapshotView={true}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-h-[calc(100vh-150px)] overflow-y-auto px-8">
        {!l1AnalysisData || loading ? (
          <div className="flex justify-center w-full">
            <Skeleton width={heightInPx} height={heightInPx} circle={true} />
          </div>
        ) : (
          <div className="m-auto mb-4">
            <ColorAnalysisSnapShot
              width={heightInPx}
              data1={l1AnalysisData}
              data2={l2AnalysisData}
              data3={l3AnalysisData}
              expandedView={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpandedViewSnapshotColorAnalysis;
